import * as THREE from 'three';
import { type Clock, type Panel, type Segment, buildLines, buildPanels, panelOutlines, polyline, ring } from './build';
import { mulberry32 } from './sky';

// Our own procedural take on ESO's Extremely Large Telescope on Cerro Armazones, built to
// its published proportions (elt.eso.org, July 2026) in metres, ground = summit platform:
//
//   dome      Ø 93 m, 80 m tall, rotating on an 11 m concrete pier; 41 m slit closed by
//             two 23 × 55 m doors that slide sideways around the sphere
//   mount     alt-az; azimuth tracks of Ø 51 / 34 / 6 m; altitude axis 33 m up;
//             Nasmyth platforms (30 × 15 m) on both ends of that axis, 27 m up
//   optics    M1 Ø 39 m, f/0.87 (R = 68.7 m) of 798 hexagonal segments around a central
//             tower carrying M3/M4/M5; M2 Ø 4.25 m, face down on a six-beam spider, 60 m
//             above the ground when pointing at the zenith
//   site      summit blasted flat into a 150 × 300 m platform at 3,046 m
//
// Everything is drawn in the landing's ink (ph26 yellow) on its background, nothing else:
// additive lines for the structure, flat-lit olive facets for the concrete annex, the drum
// and the dome (so the outside reads as ESO's photos show it), and the mirror as a
// flat-filled mosaic.

export const INK = '#eaff00';
const INK_DIM = '#7d8a00'; // the ink at ~half value, for filled parts
const GROUND = '#1a1a1a'; // theme-ph26 --background

// When each part of the build starts, in seconds since the hero mounted. The exterior
// goes up first (annex, drum, ribs under the cranes), the telescope inside it after, and the
// cladding closes over both, leaving the slit open.
export const T = {
  site: 0,
  platform: 0.1,
  cranes: 0.05,
  pier: 0.5,
  tracks: 2.3,
  structure: 2.6,
  m1: 3.4,
  m1Dur: 2.4,
  optics: 5.5,
  ribs: 5.9,
  cladding: 6.5,
  doors: 8.9,
  ready: 10.2,
} as const;

// Past every build time: a clock parked here shows the finished telescope.
export const BUILT = T.ready + 0.6;

export const M1_SEGMENTS = 798;

const DOME_R = 46.5;
const PIER_H = 11;
const DOME_CENTER_Y = 80 - DOME_R; // 33.5: the hemisphere sits on a vertical wall
const SLIT_HALF = 20.5;
const ALT_AXIS_Y = 33;
const M1_FOCAL = 34.35; // R / 2
const M1_VERTEX = -4; // below the altitude axis, in the tube frame
const M2_Y = 60 - ALT_AXIS_Y;

const v3 = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z);

// Names a piece so the /26-cl/telescope inspector can list it and toggle it on its own.
function part<O extends THREE.Object3D>(name: string, object: O): O {
  object.name = name;
  return object;
}

function sag(x: number, z: number) {
  return (x * x + z * z) / (4 * M1_FOCAL) + M1_VERTEX;
}

// ── the site ─────────────────────────────────────────────────────────────────────

const SUMMIT_R = 150;

function ridge(theta: number) {
  return 0.8 + 0.22 * Math.sin(theta * 3 + 0.6) + 0.12 * Math.sin(theta * 7 + 2.1) + 0.06 * Math.sin(theta * 13);
}

function terrainHeight(r: number, theta: number) {
  if (r <= SUMMIT_R) return 0;
  return -(((r - SUMMIT_R) / 1500) ** 1.3) * 650 * ridge(theta);
}

function terrainRadius(h: number, theta: number) {
  return SUMMIT_R + 1500 * (-h / (650 * ridge(theta))) ** (1 / 1.3);
}

function buildSite(clock: Clock) {
  const group = new THREE.Group();

  // Solid, near-black ground: hides the stars below the ridges.
  const rings = 40;
  const spokes = 96;
  const positions: number[] = [];
  const indices: number[] = [];
  for (let i = 0; i <= rings; i++) {
    const r = i === 0 ? 0 : SUMMIT_R * 0.98 + ((i - 1) / (rings - 1)) ** 1.6 * 2600;
    for (let j = 0; j < spokes; j++) {
      const theta = (j / spokes) * Math.PI * 2;
      positions.push(Math.sin(theta) * r, terrainHeight(r, theta) - 0.3, -Math.cos(theta) * r);
    }
  }
  for (let i = 0; i < rings; i++) {
    for (let j = 0; j < spokes; j++) {
      const a = i * spokes + j;
      const b = i * spokes + ((j + 1) % spokes);
      const c = (i + 1) * spokes + j;
      const d = (i + 1) * spokes + ((j + 1) % spokes);
      indices.push(a, c, b, b, c, d);
    }
  }
  const groundGeometry = new THREE.BufferGeometry();
  groundGeometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  groundGeometry.setIndex(indices);
  const ground = new THREE.Mesh(groundGeometry, new THREE.MeshBasicMaterial({ color: GROUND, side: THREE.DoubleSide }));
  ground.renderOrder = -1;
  group.add(ground);

  const contours: Segment[] = [];
  for (let k = 1; k <= 26; k++) {
    const h = -k * 22;
    const pts: THREE.Vector3[] = [];
    for (let j = 0; j < 160; j++) {
      const theta = (j / 160) * Math.PI * 2;
      const r = terrainRadius(h, theta);
      pts.push(v3(Math.sin(theta) * r, h, -Math.cos(theta) * r));
    }
    polyline(contours, pts, T.site + k * 0.07, 1.1, true);
  }
  // Survey spokes running down the slopes.
  for (let j = 0; j < 24; j++) {
    const theta = (j / 24) * Math.PI * 2;
    const pts: THREE.Vector3[] = [];
    for (let k = 0; k <= 16; k++) {
      const r = SUMMIT_R + k * 70;
      pts.push(v3(Math.sin(theta) * r, terrainHeight(r, theta), -Math.cos(theta) * r));
    }
    polyline(contours, pts, T.site + 0.2, 1.6);
  }
  group.add(buildLines(contours, clock, { color: INK, opacity: 0.16 }));

  // The access road: switchbacks down the near flank.
  const road: THREE.Vector3[] = [];
  for (let k = 0; k <= 9; k++) {
    const r = SUMMIT_R + 10 + k * 70;
    const theta = Math.PI * 0.82 + (k % 2 === 0 ? -0.09 : 0.09) - k * 0.03;
    road.push(v3(Math.sin(theta) * r, terrainHeight(r, theta) + 0.5, -Math.cos(theta) * r));
  }
  const roadSegs: Segment[] = [];
  polyline(roadSegs, road, T.site + 0.4, 1.8);

  // The platform, the dome's footprint and the survey marks.
  const site: Segment[] = [];
  const W = 150;
  const D = 75;
  polyline(site, [v3(-W, 0.1, -D), v3(W, 0.1, -D), v3(W, 0.1, D), v3(-W, 0.1, D)], T.platform, 0.9, true);
  polyline(site, ring(DOME_R, 0.1, 96), T.platform + 0.2, 0.9, true);
  site.push({ a: v3(-W - 20, 0.1, 0), b: v3(W + 20, 0.1, 0), at: T.platform, dur: 0.7 });
  site.push({ a: v3(0, 0.1, -D - 20), b: v3(0, 0.1, D + 20), at: T.platform, dur: 0.7 });
  // Ø 93 m dimension line with end ticks, along the front edge.
  const dz = 66;
  site.push({ a: v3(-DOME_R, 0.1, dz), b: v3(DOME_R, 0.1, dz), at: T.platform + 0.5, dur: 0.6 });
  for (const x of [-DOME_R, DOME_R]) site.push({ a: v3(x, 0.1, dz - 4), b: v3(x, 0.1, dz + 4), at: T.platform + 0.5, dur: 0.3 });
  // Two service buildings on the platform.
  for (const [cx, cz, w, d, h] of [
    [112, 40, 26, 16, 9],
    [-118, -42, 20, 22, 7],
  ] as const) {
    const box = boxSegments(v3(cx, 0, cz), w, h, d, T.platform + 0.8, 0.5);
    site.push(...box);
  }
  // The paved apron: a ring road around the annex, its dashed centre line, the spur out
  // to the access road, and the car park in front of the entrance hall.
  const apron: Segment[] = [];
  for (const r of [64, 76]) polyline(apron, ring(r, 0.12, 128), T.platform + 0.3, 0.9, true);
  ring(70, 0.12, 96).forEach((p, k, all) => {
    if (k % 2 === 0) apron.push({ a: p, b: all[(k + 1) % all.length], at: T.platform + 0.5, dur: 0.2 });
  });
  const spur = road[0];
  const spurDir = v3(spur.x, 0, spur.z).normalize();
  const spurSide = v3(-spurDir.z, 0, spurDir.x).multiplyScalar(5);
  for (const s of [-1, 1]) apron.push({ a: spurDir.clone().multiplyScalar(76).add(spurSide.clone().multiplyScalar(s)).setY(0.12), b: spur.clone().add(spurSide.clone().multiplyScalar(s)), at: T.platform + 0.6, dur: 0.6 });
  const hallDir = v3(Math.sin(HALL_THETA), 0, -Math.cos(HALL_THETA));
  const hallSide = v3(Math.cos(HALL_THETA), 0, Math.sin(HALL_THETA));
  const lot = (u: number, v: number) => hallDir.clone().multiplyScalar(u).add(hallSide.clone().multiplyScalar(v)).setY(0.12);
  polyline(apron, [lot(90, -30), lot(118, -30), lot(118, 30), lot(90, 30)], T.platform + 0.7, 0.6, true);
  for (let k = 1; k < 12; k++) apron.push({ a: lot(108, -30 + k * 5), b: lot(118, -30 + k * 5), at: T.platform + 0.9, dur: 0.15 });
  group.add(buildLines([...site, ...roadSegs, ...apron], clock, { color: INK, opacity: 0.5 }));

  return { group, dispose: () => disposeTree(group) };
}

function boxSegments(base: THREE.Vector3, w: number, h: number, d: number, at: number, dur: number): Segment[] {
  const out: Segment[] = [];
  const x0 = base.x - w / 2;
  const x1 = base.x + w / 2;
  const z0 = base.z - d / 2;
  const z1 = base.z + d / 2;
  const y0 = base.y;
  const y1 = base.y + h;
  const bottom = [v3(x0, y0, z0), v3(x1, y0, z0), v3(x1, y0, z1), v3(x0, y0, z1)];
  const top = bottom.map((p) => v3(p.x, y1, p.z));
  polyline(out, bottom, at, dur, true);
  bottom.forEach((p, i) => out.push({ a: p, b: top[i], at: at + dur * 0.5, dur }));
  polyline(out, top, at + dur, dur, true);
  return out;
}

// ── the dome ────────────────────────────────────────────────────────────────────

const LON = 48;
const HALL_THETA = -1.05; // the entrance hall's bearing, in ring() terms (0 = −z, the slit's side at rest)
const WALL_ROWS = [PIER_H, 22, DOME_CENTER_Y];
const LAT_STEP = 7.5;

function domePoint(lonIndex: number, y: number): THREE.Vector3;
function domePoint(lonIndex: number, y: number, latDeg: number): THREE.Vector3;
function domePoint(lonIndex: number, y: number, latDeg?: number) {
  const theta = (lonIndex / LON) * Math.PI * 2;
  if (latDeg === undefined) return v3(Math.sin(theta) * DOME_R, y, -Math.cos(theta) * DOME_R);
  const lat = THREE.MathUtils.degToRad(latDeg);
  const r = Math.cos(lat) * DOME_R;
  return v3(Math.sin(theta) * r, DOME_CENTER_Y + Math.sin(lat) * DOME_R, -Math.cos(theta) * r);
}

// The slit: 41 m wide, from a sill on the wall up over the crown to a little past the
// zenith, so the telescope can point anywhere from the horizon to straight up.
const SLIT_SILL = 20;
const SLIT_BACK = 12; // z where it ends, behind the crown

function inSlit(p: THREE.Vector3) {
  return Math.abs(p.x) < SLIT_HALF && p.z < SLIT_BACK && p.y > SLIT_SILL;
}

// A point on the shell in the vertical plane at `x`, `s` metres along it from the top of
// the front wall: negative `s` runs down the front wall, 0..πR over the hemisphere, past
// πR down the back wall. `inset` pulls it inside the shell (negative: outside).
function shellPoint(x: number, s: number, inset = 0) {
  const r = Math.sqrt(Math.max(0, (DOME_R - inset) ** 2 - x * x));
  if (s < 0) return v3(x, DOME_CENTER_Y + s, -r);
  const half = Math.PI * DOME_R;
  if (s > half) return v3(x, DOME_CENTER_Y - (s - half), r);
  const a = s / DOME_R;
  return v3(x, DOME_CENTER_Y + Math.sin(a) * r, -Math.cos(a) * r);
}

// The convex polygon cut down to the side n·p ≤ d of a plane.
function clipPolygon(vertices: THREE.Vector3[], n: THREE.Vector3, d: number) {
  const out: THREE.Vector3[] = [];
  vertices.forEach((a, i) => {
    const b = vertices[(i + 1) % vertices.length];
    const da = n.dot(a) - d;
    const db = n.dot(b) - d;
    if (da <= 0) out.push(a);
    if ((da < 0 && db > 0) || (da > 0 && db < 0)) out.push(a.clone().lerp(b, da / (da - db)));
  });
  return out;
}

function polygonArea(vertices: THREE.Vector3[]) {
  const sum = v3(0, 0, 0);
  for (let i = 1; i < vertices.length - 1; i++) {
    sum.add(vertices[i].clone().sub(vertices[0]).cross(vertices[i + 1].clone().sub(vertices[0])));
  }
  return sum.length() / 2;
}

// What is left of a shell cell once the slit is cut out of it: up to four convex pieces,
// so the slit has straight edges instead of following the cell grid.
function outsideSlit(vertices: THREE.Vector3[]) {
  const X = v3(1, 0, 0);
  const mX = v3(-1, 0, 0);
  const middle = clipPolygon(clipPolygon(vertices, X, SLIT_HALF), mX, SLIT_HALF);
  const front = clipPolygon(middle, v3(0, 0, 1), SLIT_BACK);
  return [
    clipPolygon(vertices, X, -SLIT_HALF),
    clipPolygon(vertices, mX, -SLIT_HALF),
    clipPolygon(middle, v3(0, 0, -1), -SLIT_BACK),
    clipPolygon(front, v3(0, 1, 0), SLIT_SILL),
  ].filter((p) => p.length >= 3 && polygonArea(p) > 0.05);
}

function buildDome(clock: Clock, rand: () => number) {
  const fixed = new THREE.Group(); // the concrete pier
  const rotating = new THREE.Group(); // wall + hemisphere, slit facing −z at rotation 0
  const leftDoor = new THREE.Group();
  const rightDoor = new THREE.Group();
  rotating.add(leftDoor, rightDoor);

  // Pier: vertical ribs rising all around, then its rim.
  const pier: Segment[] = [];
  for (let j = 0; j < LON; j++) {
    const base = domePoint(j, 0);
    pier.push({ a: base, b: domePoint(j, PIER_H), at: T.pier + (j % 12) * 0.03, dur: 0.6 });
  }
  polyline(pier, ring(DOME_R, PIER_H, 96), T.pier + 0.4, 0.6, true);
  polyline(pier, ring(DOME_R, PIER_H * 0.5, 96), T.pier + 0.3, 0.6, true);
  // louvre bands
  for (let j = 0; j < LON; j += 2) {
    for (const y of [3, 5, 7]) {
      const a = domePoint(j, y);
      const b = domePoint(j + 1, y);
      pier.push({ a, b, at: T.pier + 0.5 + j * 0.008, dur: 0.25 });
    }
  }
  fixed.add(part('pier · ribs', buildLines(pier, clock, { color: INK, opacity: 0.32, hot: INK })));
  // Concrete exterior: the Ø 117 m annex with its stepped flat roofs, and the drum's foot.
  // Opaque and flat-lit, so from outside the telescope reads as the building in ESO's photos.
  const AN_R = 58.5;
  const AN_H = 7;
  const STEP_R = 52;
  const STEP_H = 9.5;
  const at = (r: number, y: number, j: number) => {
    const theta = (j / LON) * Math.PI * 2;
    return v3(Math.sin(theta) * r, y, -Math.cos(theta) * r);
  };
  const pierPanels: Panel[] = [];
  const annexPanels: Panel[] = [];
  const lowered = () => v3(0, 40 + rand() * 25, 0); // lifted in by the cranes
  for (let j = 0; j < LON; j++) {
    const t0 = T.pier + 0.2 + ((j * 7) % LON) * 0.012;
    annexPanels.push({ vertices: [at(AN_R, 0, j), at(AN_R, 0, j + 1), at(AN_R, AN_H, j + 1), at(AN_R, AN_H, j)], at: t0, from: lowered() });
    annexPanels.push({ vertices: [at(AN_R, AN_H, j), at(AN_R, AN_H, j + 1), at(STEP_R, AN_H, j + 1), at(STEP_R, AN_H, j)], at: t0 + 0.25, from: lowered() });
    annexPanels.push({ vertices: [at(STEP_R, AN_H, j), at(STEP_R, AN_H, j + 1), at(STEP_R, STEP_H, j + 1), at(STEP_R, STEP_H, j)], at: t0 + 0.4, from: lowered() });
    annexPanels.push({ vertices: [at(STEP_R, STEP_H, j), at(STEP_R, STEP_H, j + 1), at(DOME_R, STEP_H + 0.4, j + 1), at(DOME_R, STEP_H + 0.4, j)], at: t0 + 0.55, from: lowered() });
    pierPanels.push({ vertices: [at(DOME_R, STEP_H, j), at(DOME_R, STEP_H, j + 1), at(DOME_R, PIER_H, j + 1), at(DOME_R, PIER_H, j)], at: t0 + 0.7, from: lowered() });
  }
  const concrete = { color: INK_DIM, rim: INK, opacity: 1, dur: 0.9, solid: true, lit: true, opaque: true } as const;
  fixed.add(part('annex', buildPanels(annexPanels, clock, { ...concrete, shade: [0.5, 0.78] })));
  fixed.add(part('pier · wall', buildPanels(pierPanels, clock, { ...concrete, shade: [0.42, 0.62] })));
  const seams = panelOutlines(annexPanels, 0.1, 0.5);
  fixed.add(part('annex · seams', buildLines(seams, clock, { color: INK, opacity: 0.22 })));

  // The entrance hall and instrument assembly area: a tall box that juts out of the
  // annex (south, downwind, on the real site), with a lower wing beside it and a large
  // door at its end.
  const hallDir = v3(Math.sin(HALL_THETA), 0, -Math.cos(HALL_THETA));
  const hallSide = v3(Math.cos(HALL_THETA), 0, Math.sin(HALL_THETA));
  const hp = (u: number, v: number, y: number) => hallDir.clone().multiplyScalar(u).add(hallSide.clone().multiplyScalar(v)).setY(y);
  const hall: Panel[] = [];
  const box = (u0: number, u1: number, v0: number, v1: number, h: number, at: number) => {
    const from = v3(0, 30, 0);
    hall.push({ vertices: [hp(u1, v0, 0), hp(u1, v1, 0), hp(u1, v1, h), hp(u1, v0, h)], at, from });
    hall.push({ vertices: [hp(u0, v0, 0), hp(u1, v0, 0), hp(u1, v0, h), hp(u0, v0, h)], at, from });
    hall.push({ vertices: [hp(u0, v1, 0), hp(u1, v1, 0), hp(u1, v1, h), hp(u0, v1, h)], at, from });
    hall.push({ vertices: [hp(u0, v0, h), hp(u1, v0, h), hp(u1, v1, h), hp(u0, v1, h)], at: at + 0.2, from });
  };
  box(48, 86, -15, 15, 14, T.pier + 0.4);
  box(54, 82, 15, 27, 8, T.pier + 0.6);
  fixed.add(part('annex · entrance hall', buildPanels(hall, clock, { ...concrete, shade: [0.55, 0.75] })));
  const hallLines = panelOutlines(hall, 0.1, 0.5);
  // the big door on its end, and roof seams
  polyline(hallLines, [hp(86.1, -6, 0), hp(86.1, -6, 10), hp(86.1, 6, 10), hp(86.1, 6, 0)], T.pier + 0.9, 0.4);
  for (let k = 1; k < 4; k++) hallLines.push({ a: hp(86.1, -6 + k * 3, 0), b: hp(86.1, -6 + k * 3, 10), at: T.pier + 1, dur: 0.3 });
  for (let k = 1; k < 6; k++) hallLines.push({ a: hp(48 + k * 6.3, -15, 14.05), b: hp(48 + k * 6.3, 15, 14.05), at: T.pier + 0.9, dur: 0.3 });
  fixed.add(part('annex · hall seams', buildLines(hallLines, clock, { color: INK, opacity: 0.3 })));

  // Ribs: meridians climb from the wall foot to the crown (or to the slit's sill).
  const ribs: Segment[] = [];
  const lats: number[] = [];
  for (let lat = 0; lat < 90; lat += LAT_STEP) lats.push(lat);
  // the stretches of a → b that are not in the slit
  const shellSeg = (a: THREE.Vector3, b: THREE.Vector3, at: number, dur: number) => {
    const n = 6;
    for (let k = 0; k < n; k++) {
      const p = a.clone().lerp(b, k / n);
      const q = a.clone().lerp(b, (k + 1) / n);
      if (!inSlit(p.clone().add(q).multiplyScalar(0.5))) ribs.push({ a: p, b: q, at: at + (dur * k) / n, dur: (dur / n) * 1.6 });
    }
  };
  for (let j = 0; j < LON; j++) {
    const pts: THREE.Vector3[] = [domePoint(j, PIER_H), domePoint(j, 22)];
    for (const lat of lats) pts.push(domePoint(j, 0, lat));
    pts.push(v3(0, 80, 0));
    const at = T.ribs + (j % 2) * 0.12 + rand() * 0.15;
    for (let i = 0; i < pts.length - 1; i++) shellSeg(pts[i], pts[i + 1], at + i * 0.1, 0.16);
  }
  // Parallels, wall rows first, then latitude rings — broken at the slit.
  const parallels: Array<{ pts: THREE.Vector3[]; at: number }> = [];
  for (const y of WALL_ROWS) parallels.push({ pts: Array.from({ length: LON + 1 }, (_, j) => domePoint(j, y)), at: T.ribs + 0.3 });
  lats.forEach((lat, i) => {
    if (lat === 0) return;
    parallels.push({ pts: Array.from({ length: LON + 1 }, (_, j) => domePoint(j, 0, lat)), at: T.ribs + 0.4 + i * 0.09 });
  });
  for (const { pts, at } of parallels) {
    for (let j = 0; j < LON; j++) shellSeg(pts[j], pts[j + 1], at + Math.abs(j - LON / 2) * 0.012, 0.12);
  }
  rotating.add(part('dome · ribs', buildLines(ribs, clock, { color: INK, opacity: 0.16 })));

  // The three structural arches, deep steel trusses: one on each side of the slit running
  // from the round girder over the crown and down the back, and one across the back
  // where the slit ends. They are what you see framing the opening in ESO's renders.
  const arches: Segment[] = [];
  const truss = (outer: (k: number) => THREE.Vector3, inner: (k: number) => THREE.Vector3, n: number, at: number) => {
    for (let k = 0; k <= n; k++) {
      const t = at + (k / n) * 1.4;
      if (k < n) {
        arches.push({ a: outer(k), b: outer(k + 1), at: t, dur: 0.12 });
        arches.push({ a: inner(k), b: inner(k + 1), at: t, dur: 0.12 });
        arches.push({ a: k % 2 ? outer(k) : inner(k), b: k % 2 ? inner(k + 1) : outer(k + 1), at: t + 0.06, dur: 0.12 });
      }
      arches.push({ a: outer(k), b: inner(k), at: t + 0.04, dur: 0.1 });
    }
  };
  const wallRun = DOME_CENTER_Y - PIER_H;
  const archLength = Math.PI * DOME_R + wallRun * 2;
  const archStations = 64;
  for (const side of [-1, 1]) {
    for (const x of [side * (SLIT_HALF + 0.6), side * (SLIT_HALF + 3.6)]) {
      const s = (k: number) => -wallRun + (k / archStations) * archLength;
      truss((k) => shellPoint(x, s(k), 0.5), (k) => shellPoint(x, s(k), 4.2), archStations, T.ribs - 0.3);
    }
    // ties between the two planes of the box section
    for (let k = 0; k <= archStations; k += 2) {
      const s = -wallRun + (k / archStations) * archLength;
      arches.push({ a: shellPoint(side * (SLIT_HALF + 0.6), s, 0.5), b: shellPoint(side * (SLIT_HALF + 3.6), s, 0.5), at: T.ribs + 0.2 + k * 0.02, dur: 0.15 });
    }
  }
  // the back arch spans side to side in the plane z = SLIT_BACK + 1
  const backZ = SLIT_BACK + 1;
  const backPoint = (t: number, inset: number) => {
    const r = Math.sqrt((DOME_R - inset) ** 2 - backZ * backZ);
    // t: 0..1 left wall up, 1..2 over the top, 2..3 right wall down
    if (t < 1) return v3(-r, PIER_H + t * wallRun, backZ);
    if (t > 2) return v3(r, DOME_CENTER_Y - (t - 2) * wallRun, backZ);
    const a = (t - 1) * Math.PI;
    return v3(-Math.cos(a) * r, DOME_CENTER_Y + Math.sin(a) * r, backZ);
  };
  truss((k) => backPoint((k / 48) * 3, 0.5), (k) => backPoint((k / 48) * 3, 4.2), 48, T.ribs);
  rotating.add(part('dome · arches', buildLines(arches, clock, { color: INK, opacity: 0.42 })));

  // Cladding: one panel per cell, sweeping up from the wall to the crown.
  const cladding: Panel[] = [];
  const rows: Array<(j: number) => THREE.Vector3[]> = [];
  for (let r = 0; r < WALL_ROWS.length - 1; r++) {
    rows.push((j) => [domePoint(j, WALL_ROWS[r]), domePoint(j + 1, WALL_ROWS[r]), domePoint(j + 1, WALL_ROWS[r + 1]), domePoint(j, WALL_ROWS[r + 1])]);
  }
  for (let i = 0; i < lats.length; i++) {
    const lo = lats[i];
    const hi = lats[i + 1];
    if (hi === undefined) {
      rows.push((j) => [domePoint(j, 0, lo), domePoint(j + 1, 0, lo), v3(0, 80, 0)]);
    } else {
      rows.push((j) => [domePoint(j, 0, lo), domePoint(j + 1, 0, lo), domePoint(j + 1, 0, hi), domePoint(j, 0, hi)]);
    }
  }
  rows.forEach((cell, rowIndex) => {
    for (let j = 0; j < LON; j++) {
      const lon = Math.abs(((j + 0.5) / LON) * 2 - 1); // 0 front, 1 back
      const at = T.cladding + rowIndex * 0.12 + lon * 0.5 + rand() * 0.12;
      for (const vertices of outsideSlit(cell(j))) {
        const c = vertices.reduce((acc, p) => acc.add(p), v3(0, 0, 0)).divideScalar(vertices.length);
        const outward = c.clone().sub(v3(0, DOME_CENTER_Y * 0.5, 0)).normalize();
        cladding.push({ vertices, at, from: outward.multiplyScalar(14 + rand() * 6) });
      }
    }
  });
  // Insulated aluminium: each panel flashes as it locks in, then reads as polished metal,
  // every facet mirroring its own band of the sky.
  const shell = { color: INK_DIM, rim: INK, opacity: 1, dur: 0.8, solid: true, lit: true, opaque: true, shade: [0.4, 0.68] as [number, number], metal: 0.6 };
  rotating.add(part('dome · cladding', buildPanels(cladding, clock, shell)));
  rotating.add(part('dome · seams', buildLines(panelOutlines(cladding, 0.15, 0.4), clock, { color: INK, opacity: 0.2 })));

  // The two slit doors, 23 × 55 m and 600 t each: thick curved shells riding proud of the
  // cladding. Closed they meet on the slit's centre line; opening, each slides straight
  // out sideways (see setDoors) and stands off the dome's flank beside the slit.
  const doorStart = -(DOME_CENTER_Y - (SLIT_SILL - 2));
  const doorEnd = DOME_R * (Math.PI - Math.acos((SLIT_BACK + 2) / DOME_R));
  const doorWidth = SLIT_HALF + 2.2;
  const doorStruts: Array<(d: number, show: boolean) => void> = [];
  for (const [side, door] of [
    [-1, leftDoor],
    [1, rightDoor],
  ] as const) {
    // the right door laps over the left one where they meet
    const outer = -4.2 - (side > 0 ? 0.3 : 0);
    const inner = -0.4 - (side > 0 ? 0.3 : 0);
    const xs = Array.from({ length: 6 }, (_, i) => side * (-0.3 + (i / 5) * (doorWidth + 0.3)));
    const steps = 18;
    const ss = Array.from({ length: steps + 1 }, (_, k) => doorStart + (k / steps) * (doorEnd - doorStart));
    const skin: Panel[] = [];
    const edges: Panel[] = [];
    const doorAt = (k: number) => T.cladding + 0.4 + (k / steps) * 0.6;
    const out = (p: THREE.Vector3) => p.clone().sub(v3(0, DOME_CENTER_Y * 0.5, 0)).normalize().multiplyScalar(10);
    for (let k = 0; k < steps; k++) {
      for (let i = 0; i < xs.length - 1; i++) {
        const vertices = [shellPoint(xs[i], ss[k], outer), shellPoint(xs[i + 1], ss[k], outer), shellPoint(xs[i + 1], ss[k + 1], outer), shellPoint(xs[i], ss[k + 1], outer)];
        skin.push({ vertices, at: doorAt(k), from: out(vertices[0]) });
      }
      // the long edges: the door's thickness, seen once it is open
      for (const x of [xs[0], xs[xs.length - 1]]) {
        const vertices = [shellPoint(x, ss[k], outer), shellPoint(x, ss[k + 1], outer), shellPoint(x, ss[k + 1], inner), shellPoint(x, ss[k], inner)];
        edges.push({ vertices, at: doorAt(k), from: out(vertices[0]) });
      }
    }
    for (const s of [ss[0], ss[steps]]) {
      for (let i = 0; i < xs.length - 1; i++) {
        const vertices = [shellPoint(xs[i], s, outer), shellPoint(xs[i + 1], s, outer), shellPoint(xs[i + 1], s, inner), shellPoint(xs[i], s, inner)];
        edges.push({ vertices, at: doorAt(s === ss[0] ? 0 : steps), from: out(vertices[0]) });
      }
    }
    // inside the door: its own ribs, visible through the slit edge when open
    const frame: Segment[] = [];
    for (let k = 0; k <= steps; k++) frame.push({ a: shellPoint(xs[0], ss[k], inner), b: shellPoint(xs[xs.length - 1], ss[k], inner), at: doorAt(k), dur: 0.3 });
    for (const x of xs) polyline(frame, ss.map((s) => shellPoint(x, s, inner)), doorAt(0), 0.8);
    // origin at the dome's centre; setDoors and setExplode move it from there
    door.position.y = DOME_CENTER_Y;
    const body = new THREE.Group();
    body.position.y = -DOME_CENTER_Y;
    // the doors are the most polished thing on the summit: they mirror almost all of it
    body.add(buildPanels(skin, clock, { ...shell, dur: 0.7, metal: 1 }));
    body.add(buildPanels(edges, clock, { ...shell, dur: 0.7, shade: [0.22, 0.3], metal: 0.9 }));
    body.add(buildLines([...panelOutlines(skin, 0.15, 0.3), ...frame], clock, { color: INK, opacity: 0.3 }));
    door.add(body);

    // The door's carriage: at four stations along it, a frame of steel struts from both
    // long edges down to the dome, X-braced, over a rail on the dome under each edge,
    // and legs from its foot to the round girder. Their far ends sit on the dome, so
    // they are recomputed as the door slides out (see setDoors) — short and hidden under
    // it when closed.
    const stations = [1, 6, 11, 17].map((k) => ss[k]);
    const rails = [xs[0], xs[xs.length - 1]];
    const pairs: Array<[THREE.Vector3, THREE.Vector3]> = [];
    const onDoor = (x: number, s: number, d: number) => shellPoint(x, s, inner).add(v3(side * d, 0, 0));
    const onDome = (q: THREE.Vector3) => {
      if (q.y < DOME_CENTER_Y) {
        const flat = Math.hypot(q.x, q.z);
        return v3((q.x / flat) * DOME_R, q.y, (q.z / flat) * DOME_R);
      }
      return q.clone().sub(v3(0, DOME_CENTER_Y, 0)).setLength(DOME_R).add(v3(0, DOME_CENTER_Y, 0));
    };
    const strutCount = stations.length * rails.length * 2 + (stations.length - 1) * rails.length * 2 + rails.length * 2;
    // each strut is drawn as two parallel lines, so it reads as a beam rather than a wire
    const strutPositions = new Float32Array(strutCount * 12);
    const strutGeometry = new THREE.BufferGeometry();
    strutGeometry.setAttribute('position', new THREE.BufferAttribute(strutPositions, 3));
    const strutMaterial = new THREE.LineBasicMaterial({ color: INK, transparent: true, opacity: 0.8, blending: THREE.AdditiveBlending, depthWrite: false });
    const struts = new THREE.LineSegments(strutGeometry, strutMaterial);
    struts.frustumCulled = false;
    rotating.add(part(side < 0 ? 'dome · left door struts' : 'dome · right door struts', struts));
    doorStruts.push((d: number, show: boolean) => {
      pairs.length = 0;
      for (const x of rails) {
        const qs = stations.map((s) => onDoor(x, s, d));
        const as = qs.map(onDome);
        qs.forEach((q, k) => {
          pairs.push([q, as[k]]);
          // the rail on the dome under this edge
          if (k < qs.length - 1) pairs.push([as[k], as[k + 1]]);
        });
        // the X-brace across to the other edge, so each station is a frame
        const other = rails[1 - rails.indexOf(x)];
        stations.forEach((s) => pairs.push([onDoor(x, s, d), onDome(onDoor(other, s, d))]));
        // legs from the door's foot down to the round girder
        const foot = qs[0];
        const flat = Math.hypot(foot.x, foot.z);
        const girder = v3((foot.x / flat) * (DOME_R + 1.1), PIER_H + 3, (foot.z / flat) * (DOME_R + 1.1));
        pairs.push([onDoor(x, ss[0], d), girder], [foot, girder]);
      }
      strutPositions.fill(0);
      const across = v3(0, 0, 0);
      pairs.slice(0, strutCount).forEach(([a, b], i) => {
        across.subVectors(b, a).cross(v3(0, 1, 0));
        if (across.lengthSq() < 1e-6) across.set(1, 0, 0);
        across.setLength(0.35);
        strutPositions.set([a.x - across.x, a.y, a.z - across.z, b.x - across.x, b.y, b.z - across.z], i * 12);
        strutPositions.set([a.x + across.x, a.y, a.z + across.z, b.x + across.x, b.y, b.z + across.z], i * 12 + 6);
      });
      strutGeometry.attributes.position.needsUpdate = true;
      struts.visible = show && d > 1.5;
    });
  }
  part('dome · left door', leftDoor);
  part('dome · right door', rightDoor);

  // The windscreen: four curved plates nested inside the lower slit. Stowed they stack at
  // the sill; they rise (a turn about the x axis through the dome's centre) to cover the
  // part of the slit below the beam when the telescope points high (see setPointing).
  const windscreen = new THREE.Group();
  const plates: THREE.Group[] = [];
  for (let i = 0; i < WINDSCREEN_PLATES; i++) {
    const inset = 2.6 + i * 0.5;
    const half = SLIT_HALF - 0.8;
    const panels: Panel[] = [];
    const xs = Array.from({ length: 7 }, (_, n) => -half + (n / 6) * half * 2);
    const as = [0, 0.5, 1].map((f) => DOME_R * THREE.MathUtils.degToRad(WINDSCREEN_FROM + f * WINDSCREEN_PLATE));
    for (let k = 0; k < as.length - 1; k++) {
      for (let n = 0; n < xs.length - 1; n++) {
        // on the sphere even below the centre: it slides inside the wall when stowed
        const sphere = (x: number, s: number) => {
          const r = Math.sqrt((DOME_R - inset) ** 2 - x * x);
          return v3(x, Math.sin(s / DOME_R) * r, -Math.cos(s / DOME_R) * r);
        };
        const vertices = [sphere(xs[n], as[k]), sphere(xs[n + 1], as[k]), sphere(xs[n + 1], as[k + 1]), sphere(xs[n], as[k + 1])];
        panels.push({ vertices, at: T.doors - 0.4 + i * 0.12, from: v3(0, -8, 0) });
      }
    }
    const plate = new THREE.Group();
    plate.position.y = DOME_CENTER_Y;
    plate.add(buildPanels(panels, clock, { ...shell, dur: 0.6, shade: [0.3, 0.42] }));
    plate.add(buildLines(panelOutlines(panels, 0.1, 0.3), clock, { color: INK, opacity: 0.35 }));
    windscreen.add(plate);
    plates.push(plate);
  }
  rotating.add(part('dome · windscreen', windscreen));

  // Ventilation louvres: rows of slatted openings in the wall beside the doors and at the
  // back, and a ring of small ones in the pier just above the auxiliary building — 89 in
  // all, as ESO counts them.
  const louvres: Panel[] = [];
  const slats: Segment[] = [];
  const louvre = (theta: number, y: number, w: number, h: number, r: number, at: number, into: Panel[], lines: Segment[]) => {
    const dir = (a: number) => v3(Math.sin(a) * r, 0, -Math.cos(a) * r);
    const da = w / 2 / r;
    const corners = [dir(theta - da).setY(y - h / 2), dir(theta + da).setY(y - h / 2), dir(theta + da).setY(y + h / 2), dir(theta - da).setY(y + h / 2)];
    into.push({ vertices: corners, at, from: dir(theta).normalize().multiplyScalar(6) });
    for (let k = 1; k < 5; k++) {
      const f = k / 5;
      lines.push({ a: corners[0].clone().lerp(corners[3], f), b: corners[1].clone().lerp(corners[2], f), at: at + 0.2, dur: 0.3 });
    }
    polyline(lines, corners, at, 0.4, true);
  };
  const deg = THREE.MathUtils.degToRad;
  for (const y of [15, 19.6, 24.2, 28.8]) {
    for (const a of [33, 40, 47]) {
      for (const side of [-1, 1]) louvre(deg(side * a), y, 4.6, 3.4, DOME_R + 0.15, T.cladding + 1.2 + a * 0.004, louvres, slats);
    }
    for (const a of [159, 166, 173, 180, 187, 194, 201]) louvre(deg(a), y, 4.6, 3.4, DOME_R + 0.15, T.cladding + 1.4, louvres, slats);
  }
  const pierLouvres: Panel[] = [];
  const pierSlats: Segment[] = [];
  for (let k = 0; k < 37; k++) louvre((k / 37) * Math.PI * 2 + 0.05, 10.25, 3.2, 1.1, DOME_R + 0.12, T.pier + 0.9 + k * 0.01, pierLouvres, pierSlats);
  const vent = { color: INK_DIM, rim: INK, opacity: 1, dur: 0.5, solid: true, lit: true, opaque: true, shade: [0.16, 0.22] as [number, number] };
  rotating.add(part('dome · louvres', buildPanels(louvres, clock, vent)));
  rotating.add(part('dome · louvre slats', buildLines(slats, clock, { color: INK, opacity: 0.5 })));
  fixed.add(part('pier · louvres', buildPanels(pierLouvres, clock, vent)));
  fixed.add(part('pier · louvre slats', buildLines(pierSlats, clock, { color: INK, opacity: 0.4 })));

  // The round girder the enclosure rides on (36 trolleys under it, on the pier at 11 m):
  // a ribbed band standing proud of the wall at its foot, with a ledge on top.
  const girder: Panel[] = [];
  const girderLines: Segment[] = [];
  const G_R = DOME_R + 1.1;
  const G_H = 3;
  for (let j = 0; j < 96; j++) {
    const a0 = (j / 96) * Math.PI * 2;
    const a1 = ((j + 1) / 96) * Math.PI * 2;
    const p = (a: number, r: number, y: number) => v3(Math.sin(a) * r, y, -Math.cos(a) * r);
    const at = T.ribs - 0.6 + (j % 24) * 0.02;
    girder.push({ vertices: [p(a0, G_R, PIER_H), p(a1, G_R, PIER_H), p(a1, G_R, PIER_H + G_H), p(a0, G_R, PIER_H + G_H)], at, from: v3(0, -6, 0) });
    girder.push({ vertices: [p(a0, G_R, PIER_H + G_H), p(a1, G_R, PIER_H + G_H), p(a1, DOME_R, PIER_H + G_H), p(a0, DOME_R, PIER_H + G_H)], at, from: v3(0, -6, 0) });
    girderLines.push({ a: p(a0, G_R + 0.05, PIER_H), b: p(a0, G_R + 0.05, PIER_H + G_H), at: at + 0.2, dur: 0.3 });
  }
  polyline(girderLines, ring(G_R + 0.05, PIER_H + G_H, 96), T.ribs - 0.4, 0.6, true);
  polyline(girderLines, ring(G_R + 0.05, PIER_H + 0.4, 96), T.ribs - 0.4, 0.6, true);
  rotating.add(part('dome · round girder', buildPanels(girder, clock, { ...shell, shade: [0.3, 0.36] })));
  rotating.add(part('dome · girder ribs', buildLines(girderLines, clock, { color: INK, opacity: 0.4 })));

  // The walkway along the slit's sill, inside the opening, with its railing.
  const walk: Panel[] = [];
  const rail: Segment[] = [];
  const sillZ = (x: number, inset: number) => -Math.sqrt((DOME_R - inset) ** 2 - x * x);
  for (let n = 0; n < 10; n++) {
    const x0 = -SLIT_HALF + (n / 10) * SLIT_HALF * 2;
    const x1 = -SLIT_HALF + ((n + 1) / 10) * SLIT_HALF * 2;
    walk.push({ vertices: [v3(x0, SLIT_SILL, sillZ(x0, 0)), v3(x1, SLIT_SILL, sillZ(x1, 0)), v3(x1, SLIT_SILL, sillZ(x1, 3.5)), v3(x0, SLIT_SILL, sillZ(x0, 3.5))], at: T.doors - 0.6, from: v3(0, -4, 0) });
  }
  const railPts = (y: number) => Array.from({ length: 21 }, (_, n) => {
    const x = -SLIT_HALF + (n / 20) * SLIT_HALF * 2;
    return v3(x, y, sillZ(x, 3.4));
  });
  polyline(rail, railPts(SLIT_SILL + 1.1), T.doors - 0.4, 0.5);
  polyline(rail, railPts(SLIT_SILL + 0.55), T.doors - 0.4, 0.5);
  for (const p of railPts(SLIT_SILL)) rail.push({ a: p, b: p.clone().setY(SLIT_SILL + 1.1), at: T.doors - 0.4, dur: 0.2 });
  rotating.add(part('dome · walkway', buildPanels(walk, clock, { ...shell, shade: [0.32, 0.38] })));
  rotating.add(part('dome · railing', buildLines([...rail, ...panelOutlines(walk, 0.1, 0.3)], clock, { color: INK, opacity: 0.55 })));

  return { fixed, rotating, leftDoor, rightDoor, plates, doorStruts };
}

// Each door slides straight out sideways, parallel to the slit, as one rigid shell: open,
// it stands off the dome's flank with its edges still parallel to the opening, as in
// ESO's renders. Its shell sits outside the dome's, so it never cuts into the cladding.
const DOOR_TRAVEL = SLIT_HALF + 3; // m, a little more than half the slit

const WINDSCREEN_PLATES = 4;
const WINDSCREEN_FROM = -24; // degrees above the dome's centre where the stowed stack starts
const WINDSCREEN_PLATE = 18; // degrees each plate covers

// ── the telescope ───────────────────────────────────────────────────────────────

function buildMount(clock: Clock) {
  const group = new THREE.Group(); // turns in azimuth
  const segs: Segment[] = [];

  // Azimuth tracks on top of the telescope pier.
  for (const [r, delay] of [
    [25.5, 0],
    [17, 0.15],
    [3, 0.3],
  ] as const) {
    polyline(segs, ring(r, PIER_H, 72), T.tracks + delay, 0.7, true);
    polyline(segs, ring(r, PIER_H + 1.2, 72), T.tracks + delay + 0.1, 0.7, true);
  }
  // the radial track and the bogies
  segs.push({ a: v3(0, PIER_H, 0), b: v3(0, PIER_H, -25.5), at: T.tracks + 0.4, dur: 0.4 });
  for (const p of ring(25.5, PIER_H, 24)) segs.push({ a: p, b: v3(p.x, PIER_H + 3, p.z), at: T.tracks + 0.5, dur: 0.25 });

  group.add(part('mount · tracks', buildLines(segs, clock, { color: INK, opacity: 0.55 })));

  // The azimuth structure: a ring-shaped floor riding the bogies, an azimuth bridge across
  // it under the tube, and trusses up to the two Nasmyth platforms. No forks: the tube
  // rocks on its cradle plates, which sit on two groups of hydrostatic pads per side.
  const floor: Segment[] = [];
  const at0 = T.tracks + 0.6;
  for (const y of [FLOOR_Y, FLOOR_Y + FLOOR_DEPTH]) {
    polyline(floor, ring(25.5, y, 72), at0, 0.7, true);
    polyline(floor, ring(17, y, 48), at0 + 0.1, 0.7, true);
  }
  const outerNodes = ring(25.5, FLOOR_Y, 24);
  const innerNodes = ring(17, FLOOR_Y, 24);
  outerNodes.forEach((o, k) => {
    const i = innerNodes[k];
    const up = (p: THREE.Vector3) => v3(p.x, FLOOR_Y + FLOOR_DEPTH, p.z);
    floor.push({ a: i, b: o, at: at0 + 0.3 + k * 0.01, dur: 0.3 });
    floor.push({ a: up(i), b: up(o), at: at0 + 0.35 + k * 0.01, dur: 0.3 });
    floor.push({ a: o, b: up(o), at: at0 + 0.4, dur: 0.2 });
    floor.push({ a: o, b: up(outerNodes[(k + 1) % 24]), at: at0 + 0.45, dur: 0.3 });
  });
  // the azimuth bridge: a box truss along x, under the tube
  const bridgeTop = FLOOR_Y + FLOOR_DEPTH; // flush with the floor: the cradle rolls just above it
  const bays = 12;
  for (const z of [-4.5, 4.5]) {
    for (const y of [FLOOR_Y, bridgeTop]) floor.push({ a: v3(-25.5, y, z), b: v3(25.5, y, z), at: at0 + 0.5, dur: 0.6 });
    for (let k = 0; k <= bays; k++) {
      const x = -25.5 + (k / bays) * 51;
      floor.push({ a: v3(x, FLOOR_Y, z), b: v3(x, bridgeTop, z), at: at0 + 0.6 + k * 0.02, dur: 0.2 });
      if (k < bays) floor.push({ a: v3(x, k % 2 ? FLOOR_Y : bridgeTop, z), b: v3(x + 51 / bays, k % 2 ? bridgeTop : FLOOR_Y, z), at: at0 + 0.65 + k * 0.02, dur: 0.2 });
    }
  }
  for (let k = 0; k <= bays; k += 2) {
    const x = -25.5 + (k / bays) * 51;
    floor.push({ a: v3(x, bridgeTop, -4.5), b: v3(x, bridgeTop, 4.5), at: at0 + 0.8, dur: 0.2 });
  }

  // Hydrostatic pads: two groups per side, under the cradle plates' rims.
  for (const side of [-1, 1]) {
    for (const zs of [-1, 1]) {
      const a = THREE.MathUtils.degToRad(CRADLE_PAD_DEG);
      const rimY = ALT_AXIS_Y - Math.cos(a) * (CRADLE_R + 0.15);
      const z = zs * Math.sin(a) * (CRADLE_R + 0.15);
      const x = side * CRADLE_X;
      floor.push(...boxSegments(v3(x, rimY - 1.6, z), 3.2, 1.6, 4.5, T.structure, 0.3));
      // pedestal down to the floor deck
      floor.push(...boxSegments(v3(x, FLOOR_Y + FLOOR_DEPTH, z), 2.2, rimY - 1.6 - FLOOR_Y - FLOOR_DEPTH, 3, T.structure - 0.2, 0.3));
    }
  }

  // Nasmyth platforms, 30 × 15 m at 27 m, each on a truss from the floor, carrying one
  // pre-focal station (where the beam arrives on the altitude axis) and three instruments.
  const nasmyth: Segment[] = [];
  for (const side of [-1, 1]) {
    const px = side * 29.5;
    nasmyth.push(...boxSegments(v3(px, 25.5, 0), 15, 1.5, 30, T.optics - 0.6, 0.4));
    for (const z of [-13, -4.5, 4.5, 13]) {
      const foot = v3(side * Math.sqrt(25.5 * 25.5 - z * z), FLOOR_Y + FLOOR_DEPTH, z);
      const inner = v3(side * 22.5, 25.5, z);
      const outer = v3(side * 36.5, 25.5, z);
      const footIn = v3(side * Math.sqrt(17 * 17 - Math.min(z * z, 200)), FLOOR_Y + FLOOR_DEPTH, z);
      nasmyth.push({ a: foot, b: inner, at: T.structure + 0.4, dur: 0.5 });
      nasmyth.push({ a: foot, b: outer, at: T.structure + 0.5, dur: 0.6 });
      nasmyth.push({ a: footIn, b: inner, at: T.structure + 0.6, dur: 0.5 });
    }
    for (const [z0, z1] of [
      [-13, -4.5],
      [4.5, 13],
    ]) {
      nasmyth.push({ a: v3(side * 22.5, 25.5, z0), b: v3(side * Math.sqrt(25.5 * 25.5 - z1 * z1), FLOOR_Y + FLOOR_DEPTH, z1), at: T.structure + 0.7, dur: 0.5 });
    }

    // the pre-focal station: a drum on the axis, its rotator ring facing the tube
    const ps = side * 24.5;
    nasmyth.push(...cylinderSegments(v3(ps, 27, 0), 2.6, 6, T.optics - 0.3, 0.4, 'x', side));
    polyline(nasmyth, Array.from({ length: 24 }, (_, k) => v3(side * 22.2, ALT_AXIS_Y + Math.sin((k / 24) * Math.PI * 2) * 2.6, Math.cos((k / 24) * Math.PI * 2) * 2.6)), T.optics, 0.4, true);
    // HARMONI: a big framed box; MICADO: a tall cryostat on legs; METIS: a box with its
    // round cryostat on top
    nasmyth.push(...boxSegments(v3(px + side * 2, 27, -9.5), 9, 8, 8, T.optics + 0.4, 0.35));
    nasmyth.push({ a: v3(px + side * 2 - 4.5, 27, -13.5), b: v3(px + side * 2 + 4.5, 35, -5.5), at: T.optics + 0.7, dur: 0.3 });
    nasmyth.push(...cylinderSegments(v3(px + side * 2, 27, 1.5), 2.8, 8.5, T.optics + 0.5, 0.35, 'y'));
    nasmyth.push(...boxSegments(v3(px + side * 2, 27, 10.5), 6, 4, 6, T.optics + 0.6, 0.35));
    nasmyth.push(...cylinderSegments(v3(px + side * 2, 31, 10.5), 2.2, 3.5, T.optics + 0.8, 0.3, 'y'));
  }
  group.add(part('mount · azimuth floor, bridge, pads', buildLines(floor, clock, { color: INK, opacity: 0.5 })));
  group.add(part('mount · Nasmyth platforms, instruments', buildLines(nasmyth, clock, { color: INK, opacity: 0.55 })));
  return group;
}

const FLOOR_Y = PIER_H + 3; // the azimuth floor, on the bogies
const FLOOR_DEPTH = 2.5;
// The cradle: two semicircular plates on the M1 cell, centred on the altitude axis.
const CRADLE_R = 16;
const CRADLE_X = 17.5;
const CRADLE_PAD_DEG = 28; // each pad group sits this far either side of the bottom

// The tube, in its own frame (origin on the altitude axis, +y the optical axis).
const TUBE_N = 12; // sides of the cell, the truss and the top ring
const CELL_R = 21.5;
const CELL_UPPER = -5.5;
const CELL_WALK = -8;
const CELL_LOWER = -11;
const TOP_Y = 21.5;
const TOP_R = 16;
const TOP_DEPTH = 2.4;
// where the four laser guide star units sit on the top ring (mid-side of its polygon)
const LASER_R = TOP_R * Math.cos(Math.PI / TUBE_N) - 1.1;
const LASER_LAUNCH = [v3(LASER_R, 0, 0), v3(-LASER_R, 0, 0), v3(0, 0, LASER_R), v3(0, 0, -LASER_R)];

// A cylinder as lines: two rims and a few generators. `axis` is its direction, from `base`.
function cylinderSegments(base: THREE.Vector3, radius: number, length: number, at: number, dur: number, axis: 'x' | 'y', sign = 1): Segment[] {
  const out: Segment[] = [];
  const n = 20;
  const rim = (t: number) =>
    Array.from({ length: n }, (_, k) => {
      const a = (k / n) * Math.PI * 2;
      const u = Math.cos(a) * radius;
      const w = Math.sin(a) * radius;
      return axis === 'y' ? v3(base.x + u, base.y + t, base.z + w) : v3(base.x + t * sign, base.y + radius + u, base.z + w);
    });
  const lo = rim(0);
  const hi = rim(length);
  polyline(out, lo, at, dur, true);
  polyline(out, hi, at + dur, dur, true);
  for (let k = 0; k < n; k += 4) out.push({ a: lo[k], b: hi[k], at: at + dur * 0.5, dur });
  return out;
}

function hexVertices(cx: number, cz: number, radius: number) {
  return Array.from({ length: 6 }, (_, k) => {
    const a = Math.PI / 6 + (k * Math.PI) / 3;
    const x = cx + Math.cos(a) * radius;
    const z = cz + Math.sin(a) * radius;
    return v3(x, sag(x, z), z);
  });
}

export function m1SegmentCenters() {
  const pitch = 1.26;
  const centers: Array<{ x: number; z: number; r: number; a: number }> = [];
  const n = 20;
  for (let q = -n; q <= n; q++) {
    for (let r = -n; r <= n; r++) {
      const x = pitch * (q + r / 2);
      const z = pitch * ((r * Math.sqrt(3)) / 2);
      const d = Math.hypot(x, z);
      if (d < 4.6) continue; // the central obstruction around the M3/M4 tower
      centers.push({ x, z, r: d, a: Math.atan2(z, x) });
    }
  }
  centers.sort((p, q) => p.r - q.r);
  return { centers: centers.slice(0, M1_SEGMENTS), pitch };
}

function buildTube(clock: Clock, rand: () => number) {
  const group = new THREE.Group(); // pivots on the altitude axis; +y is the optical axis
  group.position.y = ALT_AXIS_Y;

  // M1: the segments rain down onto the cell, its six sectors of 133 filling outward
  // together.
  const { centers, pitch } = m1SegmentCenters();
  const hexR = pitch / Math.sqrt(3) - 0.11; // ~20 cm joints, wide enough to read
  const sectorOf = (a: number) => Math.min(5, Math.floor(((a + Math.PI) / (Math.PI * 2)) * 6));
  const rankInSector = new Map<(typeof centers)[number], number>();
  for (let s = 0; s < 6; s++) {
    centers.filter((c) => sectorOf(c.a) === s).forEach((c, i) => rankInSector.set(c, i));
  }
  const perSector = M1_SEGMENTS / 6;
  const segments: Panel[] = centers.map((c) => {
    const outward = v3(c.x, 0, c.z).normalize();
    return {
      vertices: hexVertices(c.x, c.z, hexR),
      at: T.m1 + ((rankInSector.get(c) ?? 0) / perSector) * T.m1Dur + sectorOf(c.a) * 0.05 + rand() * 0.12,
      from: v3(outward.x * 8, 22 + rand() * 14, outward.z * 8),
    };
  });
  // Each segment falls lit, lands, and cools to its own shade of the ink.
  // Coated glass: every segment mirrors the dome above it — dark ribs, and the slit's
  // bright band of sky — and the paraboloid's curvature tilts each one a little more
  // towards the rim, so the reflection breaks up across the mosaic.
  const mirror = buildPanels(segments, clock, { color: INK_DIM, rim: INK, opacity: 0.94, sheen: 0.3, dur: 0.7, solid: true, lit: true, shade: [0.45, 1], metal: 0.95, interior: true });
  mirror.renderOrder = 2;
  group.add(part('tube · M1 (798 segments)', mirror));

  // The M1 cell: a three-level truss, polygonal in plan. The upper level carries the
  // segments, the walkable level has ring and radial corridors, and the lower lattice
  // stiffens it and carries the central tower.
  const cell: Segment[] = [];
  const phase = Math.PI / TUBE_N;
  const levels = [CELL_UPPER, CELL_WALK, CELL_LOWER].map((y) => ring(CELL_R, y, TUBE_N, phase));
  levels.forEach((pts, li) => polyline(cell, pts, T.structure + 0.2 + li * 0.15, 0.6, true));
  for (let li = 0; li < levels.length - 1; li++) {
    for (let k = 0; k < TUBE_N; k++) {
      const at = T.structure + 0.3 + li * 0.15 + k * 0.02;
      cell.push({ a: levels[li][k], b: levels[li + 1][k], at, dur: 0.3 });
      cell.push({ a: levels[li][k], b: levels[li + 1][(k + 1) % TUBE_N], at: at + 0.05, dur: 0.3 });
    }
  }
  const inscribed = CELL_R * Math.cos(phase) - 0.6;
  for (let g = -4; g <= 4; g++) {
    const c = g * 4.4;
    const half = Math.sqrt(Math.max(0, inscribed * inscribed - c * c));
    const at = T.structure + 0.2 + Math.abs(g) * 0.04;
    cell.push({ a: v3(c, CELL_LOWER, -half), b: v3(c, CELL_LOWER, half), at, dur: 0.5 });
    cell.push({ a: v3(-half, CELL_LOWER, c), b: v3(half, CELL_LOWER, c), at, dur: 0.5 });
    cell.push({ a: v3(c, CELL_UPPER, -half), b: v3(c, CELL_UPPER, half), at: at + 0.3, dur: 0.5 });
  }
  for (const r of [7, 14]) polyline(cell, ring(r, CELL_WALK, 48), T.structure + 0.5, 0.6, true);
  for (const p of ring(inscribed, CELL_WALK, 6, phase)) cell.push({ a: p.clone().multiplyScalar(0.25).setY(CELL_WALK), b: p, at: T.structure + 0.55, dur: 0.4 });
  // posts from the upper level to the mirror's back
  for (const r of [9, 15, 20]) {
    for (const p of ring(r, CELL_UPPER, TUBE_N, phase)) cell.push({ a: p, b: v3(p.x, sag(p.x, p.z) - 0.35, p.z), at: T.structure + 0.6, dur: 0.2 });
  }
  group.add(part('tube · M1 cell', buildLines(cell, clock, { color: INK, opacity: 0.45 })));

  // The cradle: a semicircular plate on each side of the cell, centred on the altitude
  // axis, its rim rolling on the hydrostatic pads as the tube tilts. Perforated, to let
  // air flush past the mirror.
  const cradle: Panel[] = [];
  const cradleLines: Segment[] = [];
  const PLATE_W = 5.5;
  for (const side of [-1, 1]) {
    const x = side * CRADLE_X;
    // ψ from the tube's −y; a little more than a half circle, so the rim stays on both pad
    // groups from the horizon to the zenith
    const at = (psi: number, r: number) => v3(x, -Math.cos(psi) * r, Math.sin(psi) * r);
    const n = 24;
    const span = THREE.MathUtils.degToRad(118);
    for (let k = 0; k < n; k++) {
      const p0 = -span + (k / n) * span * 2;
      const p1 = -span + ((k + 1) / n) * span * 2;
      const t = T.structure + 0.5 + Math.abs(k - n / 2) * 0.03;
      cradle.push({ vertices: [at(p0, CRADLE_R - PLATE_W), at(p1, CRADLE_R - PLATE_W), at(p1, CRADLE_R), at(p0, CRADLE_R)], at: t, from: v3(side * 10, -6, 0) });
      if (k % 2 === 0) {
        const c = at((p0 + p1) / 2, CRADLE_R - PLATE_W / 2);
        polyline(cradleLines, Array.from({ length: 12 }, (_, m) => v3(x + side * 0.05, c.y + Math.sin((m / 12) * Math.PI * 2) * 1.3, c.z + Math.cos((m / 12) * Math.PI * 2) * 1.3)), t + 0.4, 0.3, true);
      }
    }
    // spokes from the plate's inner edge up into the cell
    for (const deg of [-75, -45, -15, 15, 45, 75]) {
      const psi = THREE.MathUtils.degToRad(deg);
      cradleLines.push({ a: at(psi, CRADLE_R - PLATE_W), b: v3(x, Math.max(CELL_LOWER, -Math.cos(psi) * 9), Math.sin(psi) * 9), at: T.structure + 0.8, dur: 0.3 });
    }
  }
  const metal = { color: INK_DIM, rim: INK, opacity: 1, dur: 0.7, solid: true, lit: true, opaque: true, shade: [0.38, 0.5] as [number, number], metal: 0.6 };
  group.add(part('tube · cradle plates', buildPanels(cradle, clock, metal)));
  group.add(part('tube · cradle edges', buildLines([...cradleLines, ...panelOutlines(cradle, 0.1, 0.3)], clock, { color: INK, opacity: 0.4 })));

  // The tube: an open truss of tubular members, twelve-sided, rising ~27 m from the M1
  // cell to the polygonal top ring.
  const segs: Segment[] = [];
  const tube = [
    { y: sag(CELL_R, 0) - 0.3, r: CELL_R },
    { y: 10, r: 19.5 },
    { y: TOP_Y, r: TOP_R },
  ].map(({ y, r }) => ring(r, y, TUBE_N, phase));
  tube.forEach((pts, li) => polyline(segs, pts, T.structure + 0.9 + li * 0.35, 0.6, true));
  levels[0].forEach((p, k) => segs.push({ a: p, b: tube[0][k], at: T.structure + 0.8, dur: 0.3 }));
  for (let li = 0; li < tube.length - 1; li++) {
    for (let k = 0; k < TUBE_N; k++) {
      const at = T.structure + 1.0 + li * 0.35 + k * 0.02;
      const next = (k + 1) % TUBE_N;
      segs.push({ a: tube[li][k], b: tube[li + 1][k], at, dur: 0.45 });
      segs.push({ a: tube[li][k], b: tube[li + 1][next], at: at + 0.1, dur: 0.45 });
      segs.push({ a: tube[li][next], b: tube[li + 1][k], at: at + 0.1, dur: 0.45 });
    }
  }
  // the top ring: a deep polygonal box section
  const topRings = [
    ring(TOP_R, TOP_Y + TOP_DEPTH, TUBE_N, phase),
    ring(TOP_R - 2.2, TOP_Y, TUBE_N, phase),
    ring(TOP_R - 2.2, TOP_Y + TOP_DEPTH, TUBE_N, phase),
  ];
  topRings.forEach((pts) => polyline(segs, pts, T.optics - 0.5, 0.6, true));
  const ringCorners = [tube[2], topRings[0], topRings[2], topRings[1]];
  for (let k = 0; k < TUBE_N; k++) {
    polyline(segs, ringCorners.map((pts) => pts[k]), T.optics - 0.3 + k * 0.02, 0.3, true);
    segs.push({ a: tube[2][k], b: topRings[0][(k + 1) % TUBE_N], at: T.optics - 0.2, dur: 0.3 });
  }
  // Spider: six deep beams from the top ring to the M2 crown, which holds M2 face down.
  const hubLow = ring(2.6, M2_Y + 0.5, 6, phase);
  const hubHigh = ring(2.6, M2_Y + 2.5, 6, phase);
  const crown = ring(4.2, M2_Y + 3.6, 6, phase);
  hubLow.forEach((h, k) => {
    segs.push({ a: topRings[1][k * 2], b: h, at: T.optics, dur: 0.5 });
    segs.push({ a: topRings[2][k * 2], b: hubHigh[k], at: T.optics + 0.05, dur: 0.5 });
    segs.push({ a: h, b: hubHigh[k], at: T.optics + 0.4, dur: 0.3 });
    segs.push({ a: hubHigh[k], b: crown[k], at: T.optics + 0.5, dur: 0.3 });
  });
  for (const pts of [hubLow, hubHigh, crown]) polyline(segs, pts, T.optics + 0.3, 0.4, true);
  // Four laser launch units on the top ring.
  for (const p of LASER_LAUNCH) segs.push(...boxSegments(v3(p.x, TOP_Y + TOP_DEPTH, p.z), 2.4, 3.2, 2.4, T.optics + 0.6, 0.3));
  // Central tower (10 m) carrying M3, M4 and M5, on legs from the cell's lower lattice,
  // with its own six-beam spider at two-thirds height, hidden in the shadow of M2's.
  const towerBase = ring(1.8, M1_VERTEX - 2, 4, Math.PI / 4);
  const towerTop = ring(1.8, M1_VERTEX + 10, 4, Math.PI / 4);
  const towerFeet = ring(3.2, CELL_LOWER, 4, Math.PI / 4);
  towerBase.forEach((b, k) => {
    segs.push({ a: towerFeet[k], b, at: T.optics, dur: 0.3 });
    segs.push({ a: b, b: towerTop[k], at: T.optics + 0.1, dur: 0.6 });
    segs.push({ a: b, b: towerTop[(k + 1) % 4], at: T.optics + 0.2, dur: 0.6 });
  });
  polyline(segs, towerTop, T.optics + 0.6, 0.3, true);
  const towerSpiderY = M1_VERTEX - 2 + (12 * 2) / 3;
  for (const p of ring(7.5, 0, 6, phase)) {
    const hub = p.clone().setLength(1.8).setY(towerSpiderY);
    segs.push({ a: hub, b: v3(p.x, sag(p.x, p.z) + 0.15, p.z), at: T.optics + 0.7, dur: 0.4 });
  }
  group.add(part('tube · truss, top ring, spider, tower', buildLines(segs, clock, { color: INK, opacity: 0.5 })));

  const disc = (r: number, y: number, tilt = 0, count = 28): THREE.Vector3[] =>
    Array.from({ length: count }, (_, k) => {
      const a = (k / count) * Math.PI * 2;
      const x = Math.cos(a) * r;
      const z = Math.sin(a) * r;
      return v3(x, y + x * Math.tan(tilt), z);
    });
  const optics: Panel[] = [
    { vertices: disc(2.125, M2_Y, 0), at: T.optics + 0.5, from: v3(0, 12, 0) }, // M2
    { vertices: disc(2.0, M1_VERTEX - 1.5, 0), at: T.optics + 0.6, from: v3(0, -6, 0) }, // M3
    { vertices: disc(1.2, M1_VERTEX + 10, 0.25), at: T.optics + 0.8, from: v3(0, 8, 0) }, // M4
    { vertices: disc(1.1, 0, Math.PI / 4, 20), at: T.optics + 0.9, from: v3(0, 6, 0) }, // M5
  ];
  group.add(part('tube · M2–M5', buildPanels(optics, clock, { color: INK, rim: INK, opacity: 0.9, dur: 0.6, solid: true, lit: true, shade: [0.8, 1], metal: 0.95, interior: true })));
  group.add(part('tube · M2–M5 rims', buildLines(panelOutlines(optics, 0.1, 0.3), clock, { color: INK, opacity: 0.7 })));

  return group;
}

// The light path, M1 → M2 → M3 → M4 → M5 → Nasmyth, as dashes flowing along the rays.
const RAY_VERTEX = `
  attribute float aDist;
  varying float vDist;
  void main() {
    vDist = aDist;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }`;
const RAY_FRAGMENT = `
  uniform float uTime;
  uniform float uOpacity;
  uniform vec3 uColor;
  varying float vDist;
  void main() {
    float dash = smoothstep(0.0, 0.15, fract((vDist - uTime * 26.0) / 9.0)) * (1.0 - smoothstep(0.35, 0.5, fract((vDist - uTime * 26.0) / 9.0)));
    float a = (0.25 + dash) * uOpacity;
    gl_FragColor = vec4(uColor * a, a);
  }`;

function buildLightPath() {
  const positions: number[] = [];
  const dists: number[] = [];
  const m3 = v3(0, M1_VERTEX - 1.5, 0);
  const m4 = v3(0, M1_VERTEX + 10, 0);
  const m5 = v3(0, 0, 0);
  for (let k = 0; k < 6; k++) {
    const a = (k / 6) * Math.PI * 2 + 0.3;
    const rx = Math.cos(a) * 14;
    const rz = Math.sin(a) * 14;
    const onM1 = v3(rx, sag(rx, rz), rz);
    const onM2 = v3(Math.cos(a) * 1.4, M2_Y, Math.sin(a) * 1.4);
    const nasmyth = v3(k % 2 ? 26 : -26, 0, 0);
    const path = [v3(rx, 70, rz), onM1, onM2, m3.clone().add(v3(Math.cos(a) * 0.8, 0, Math.sin(a) * 0.8)), m4, m5, nasmyth];
    let d = 0;
    for (let i = 0; i < path.length - 1; i++) {
      const len = path[i].distanceTo(path[i + 1]);
      positions.push(path[i].x, path[i].y, path[i].z, path[i + 1].x, path[i + 1].y, path[i + 1].z);
      dists.push(d, d + len);
      d += len;
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('aDist', new THREE.Float32BufferAttribute(dists, 1));
  const material = new THREE.ShaderMaterial({
    uniforms: { uTime: { value: 0 }, uOpacity: { value: 0 }, uColor: { value: new THREE.Color(INK) } },
    vertexShader: RAY_VERTEX,
    fragmentShader: RAY_FRAGMENT,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const lines = new THREE.LineSegments(geometry, material);
  lines.frustumCulled = false;
  return { lines, material };
}

// Four sodium laser guide stars fired from the launch units on the top ring.
const LASER_VERTEX = `
  varying float vAlong;
  varying vec3 vNormal;
  varying vec3 vView;
  void main() {
    vAlong = uv.y;
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    vNormal = normalize(normalMatrix * normal);
    vView = normalize(-mv.xyz);
    gl_Position = projectionMatrix * mv;
  }`;
const LASER_FRAGMENT = `
  uniform float uOpacity;
  uniform float uTime;
  uniform vec3 uColor;
  varying float vAlong;
  varying vec3 vNormal;
  varying vec3 vView;
  void main() {
    float core = pow(abs(dot(normalize(vNormal), normalize(vView))), 1.6);
    float fade = smoothstep(0.0, 0.02, vAlong) * (1.0 - smoothstep(0.35, 1.0, vAlong));
    float flicker = 0.85 + 0.15 * sin(uTime * 37.0 + vAlong * 80.0);
    float a = core * fade * flicker * uOpacity;
    gl_FragColor = vec4(uColor * a, a);
  }`;

function buildLasers() {
  const group = new THREE.Group();
  const material = new THREE.ShaderMaterial({
    uniforms: { uOpacity: { value: 0 }, uTime: { value: 0 }, uColor: { value: new THREE.Color(INK) } },
    vertexShader: LASER_VERTEX,
    fragmentShader: LASER_FRAGMENT,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const length = 1600;
  const geometry = new THREE.CylinderGeometry(3.2, 1.6, length, 12, 1, true);
  geometry.translate(0, length / 2, 0);
  for (const p of LASER_LAUNCH) {
    const beam = new THREE.Mesh(geometry, material);
    beam.position.set(p.x, TOP_Y + TOP_DEPTH + 3.2, p.z);
    // converge on a common point ~1.5 km up the optical axis
    beam.lookAt(0, 1500, 0);
    beam.rotateX(Math.PI / 2);
    beam.frustumCulled = false;
    group.add(beam);
  }
  return { group, material, geometry };
}

// ── the cranes ──────────────────────────────────────────────────────────────────

// Lattice tower cranes around the dome, as in the construction photos: a square mast, a
// jib with its counter-jib and A-frame, and a hook that trolleys in and out and rides up
// and down. The mast and jib grow with the build clock; the hook is animated by hand.
const CRANES = [
  { x: -88, z: 28, h: 118, reach: 66, phi: 0.7, delay: 0 },
  { x: 86, z: 56, h: 104, reach: 60, phi: 2.5, delay: 0.25 },
  { x: -66, z: -76, h: 112, reach: 64, phi: 4.1, delay: 0.45 },
  { x: 98, z: -48, h: 94, reach: 54, phi: 5.4, delay: 0.7 },
] as const;

function buildCranes(clock: Clock) {
  const group = new THREE.Group();
  const lineMaterials: THREE.ShaderMaterial[] = [];
  const hookMaterial = new THREE.LineBasicMaterial({ color: INK, transparent: true, opacity: 0.8, blending: THREE.AdditiveBlending, depthWrite: false });
  const hookBlock = new THREE.BufferGeometry().setFromPoints(
    boxSegments(v3(0, -1.2, 0), 2.4, 2.4, 2.4, 0, 0).flatMap((s) => [s.a, s.b]),
  );
  const cableGeometry = new THREE.BufferGeometry().setFromPoints([v3(0, 0, 0), v3(0, -1, 0)]);
  const rigs: Array<{ jib: THREE.Group; hook: THREE.Group; cable: THREE.LineSegments; reach: number; phi: number; phase: number }> = [];

  CRANES.forEach((spec, k) => {
    const crane = new THREE.Group();
    crane.position.set(spec.x, 0, spec.z);
    const w = 1.7;
    const step = 4.5;
    const levels = Math.floor(spec.h / step);
    const at0 = T.cranes + spec.delay;
    const sq = (y: number) => [v3(-w, y, -w), v3(w, y, -w), v3(w, y, w), v3(-w, y, w)];
    const mast: Segment[] = [];
    for (let lv = 0; lv < levels; lv++) {
      const lo = sq(lv * step);
      const hi = sq((lv + 1) * step);
      const at = at0 + lv * 0.04;
      for (let i = 0; i < 4; i++) {
        mast.push({ a: lo[i], b: hi[i], at, dur: 0.3 });
        mast.push({ a: lo[i], b: hi[(i + 1) % 4], at: at + 0.05, dur: 0.3 });
        mast.push({ a: hi[i], b: hi[(i + 1) % 4], at: at + 0.1, dur: 0.2 });
      }
    }
    const mastLines = buildLines(mast, clock, { color: INK, opacity: 0.5 });
    crane.add(mastLines);
    lineMaterials.push(mastLines.material as THREE.ShaderMaterial);

    const jib = new THREE.Group();
    jib.position.y = levels * step;
    const counter = spec.reach * 0.3;
    const jibSegs: Segment[] = [];
    const jibAt = at0 + levels * 0.04 + 0.2;
    const stations: number[] = [];
    for (let x = -counter; x < spec.reach; x += 4.5) stations.push(x);
    stations.push(spec.reach);
    const section = (x: number) => [v3(x, 0, -1.4), v3(x, 0, 1.4), v3(x, 2.8, 0)];
    stations.forEach((x, i) => {
      const a = section(x);
      const at = jibAt + i * 0.025;
      polyline(jibSegs, a, at, 0.2, true);
      if (i > 0) {
        const b = section(stations[i - 1]);
        for (let c = 0; c < 3; c++) jibSegs.push({ a: b[c], b: a[c], at: at + 0.05, dur: 0.2 });
        jibSegs.push({ a: b[0], b: a[2], at: at + 0.1, dur: 0.2 });
        jibSegs.push({ a: b[1], b: a[2], at: at + 0.1, dur: 0.2 });
      }
    });
    // A-frame and the pendants that hold the jib and counter-jib up
    const apex = v3(0, 15, 0);
    for (const z of [-1.4, 1.4]) jibSegs.push({ a: v3(0, 0, z), b: apex, at: jibAt + 0.3, dur: 0.4 });
    jibSegs.push({ a: apex, b: v3(spec.reach, 2.8, 0), at: jibAt + 0.6, dur: 0.6 });
    jibSegs.push({ a: apex, b: v3(spec.reach * 0.55, 2.8, 0), at: jibAt + 0.6, dur: 0.5 });
    jibSegs.push({ a: apex, b: v3(-counter, 2.8, 0), at: jibAt + 0.6, dur: 0.4 });
    jibSegs.push(...boxSegments(v3(-counter + 3, 0, 0), 5, 5, 3.6, jibAt + 0.5, 0.3)); // counterweight
    jibSegs.push(...boxSegments(v3(2.5, -2.6, 0), 3.4, 2.6, 2.8, jibAt + 0.4, 0.3)); // cab
    const jibLines = buildLines(jibSegs, clock, { color: INK, opacity: 0.55 });
    jib.add(jibLines);
    lineMaterials.push(jibLines.material as THREE.ShaderMaterial);

    const hook = new THREE.Group();
    const cable = new THREE.LineSegments(cableGeometry, hookMaterial);
    cable.frustumCulled = false;
    const block = new THREE.LineSegments(hookBlock, hookMaterial);
    block.frustumCulled = false;
    hook.add(cable, block);
    jib.add(hook);
    crane.add(jib);
    group.add(crane);
    rigs.push({ jib, hook, cable, reach: spec.reach, phi: spec.phi, phase: k * 1.7 });
  });

  const update = (time: number, amount: number) => {
    group.visible = amount > 0.002;
    for (const m of lineMaterials) m.uniforms.uOpacity.value = (m.userData.base ??= m.uniforms.uOpacity.value) * amount;
    hookMaterial.opacity = 0.8 * amount;
    const grown = clamp01((time - T.cranes - 3) / 1.5);
    for (const rig of rigs) {
      rig.jib.rotation.y = rig.phi + 0.22 * Math.sin(time * 0.21 + rig.phase);
      const trolley = rig.reach * (0.5 + 0.28 * Math.sin(time * 0.33 + rig.phase));
      const drop = 22 + 30 * (0.5 + 0.5 * Math.sin(time * 0.29 + rig.phase * 1.3));
      rig.hook.position.set(trolley, 0, 0);
      rig.hook.visible = grown > 0;
      rig.cable.scale.y = drop;
      // the block hangs at the end of the cable
      (rig.hook.children[1] as THREE.Object3D).position.y = -drop;
    }
  };

  return {
    group,
    update,
    dispose: () => {
      disposeTree(group);
      hookBlock.dispose();
      cableGeometry.dispose();
      hookMaterial.dispose();
    },
  };
}

const clamp01 = (x: number) => Math.min(1, Math.max(0, x));

// ── assembly ────────────────────────────────────────────────────────────────────

export type EltModel = {
  root: THREE.Group;
  // every named piece, in scene order (see `part`)
  parts: THREE.Object3D[];
  setPointing: (azDeg: number, altDeg: number, domeAzDeg: number) => void;
  setDoors: (open: number) => void;
  // 0 = built, 1 = every layer pulled apart along the vertical
  setExplode: (k: number) => void;
  // crane animation: `amount` 0..1 fades them (they come down once the telescope is done)
  setCranes: (time: number, amount: number) => void;
  setLasers: (opacity: number, time: number) => void;
  setLightPath: (opacity: number, time: number) => void;
  // world positions of the four laser launch units' muzzles, written into `out`
  laserMuzzles: (out: THREE.Vector3[]) => void;
  dispose: () => void;
};

export function createElt(clock: Clock): EltModel {
  const rand = mulberry32(798);
  const root = new THREE.Group();

  const site = buildSite(clock);
  root.add(part('site', site.group));

  const dome = buildDome(clock, rand);
  const domeAz = new THREE.Group();
  domeAz.add(dome.rotating);
  root.add(dome.fixed, domeAz);

  const mountAz = new THREE.Group();
  mountAz.add(buildMount(clock));
  const tube = buildTube(clock, rand);
  mountAz.add(tube);
  const cranes = buildCranes(clock);
  root.add(part('cranes', cranes.group));
  const path = buildLightPath();
  tube.add(path.lines);
  const lasers = buildLasers();
  tube.add(lasers.group);
  root.add(mountAz);

  // the optics mirror the dome's interior, so they need to know where its slit faces
  const opticsMaterials: THREE.ShaderMaterial[] = [];
  tube.traverse((child) => {
    if (child.name === 'tube · M1 (798 segments)' || child.name === 'tube · M2–M5') opticsMaterials.push((child as THREE.Mesh).material as THREE.ShaderMaterial);
  });

  const setPointing = (azDeg: number, altDeg: number, domeAzDeg: number) => {
    mountAz.rotation.y = -THREE.MathUtils.degToRad(azDeg);
    domeAz.rotation.y = -THREE.MathUtils.degToRad(domeAzDeg);
    for (const m of opticsMaterials) {
      m.uniforms.uSlitAcross.value.set(1, 0, 0).applyAxisAngle(v3(0, 1, 0), domeAz.rotation.y);
      m.uniforms.uSlitForward.value.set(0, 0, -1).applyAxisAngle(v3(0, 1, 0), domeAz.rotation.y);
    }
    tube.rotation.x = -(Math.PI / 2 - THREE.MathUtils.degToRad(altDeg));
    // The windscreen's top stays ~55° under the line of sight: stowed at the sill for low
    // pointings, rising as the telescope looks higher (and never hiding it from the front).
    const top = altDeg - 55;
    const reach = THREE.MathUtils.clamp((top - WINDSCREEN_FROM - WINDSCREEN_PLATE) / (WINDSCREEN_PLATE * (WINDSCREEN_PLATES - 1)), 0, 1);
    dome.plates.forEach((plate, i) => {
      plate.rotation.x = THREE.MathUtils.degToRad(i * WINDSCREEN_PLATE * reach);
    });
  };

  // the doors move both when they open and when the explode pulls them apart
  let doorsOpen = 0;
  let exploded = 0;
  const placeDoors = () => {
    const x = doorsOpen * DOOR_TRAVEL + exploded * 60;
    dome.leftDoor.position.set(-x, DOME_CENTER_Y + exploded * 20, 0);
    dome.rightDoor.position.set(x, DOME_CENTER_Y + exploded * 20, 0);
    // the struts only make sense with the doors on their rails and built
    const show = exploded < 0.01 && clock.value > T.cladding + 1;
    for (const update of dome.doorStruts) update(doorsOpen * DOOR_TRAVEL, show);
  };

  const setDoors = (open: number) => {
    doorsOpen = open;
    placeDoors();
  };

  const setExplode = (k: number) => {
    domeAz.position.y = k * 120;
    mountAz.position.y = k * 26;
    tube.position.y = ALT_AXIS_Y + k * 90;
    exploded = k;
    placeDoors();
  };

  const parts: THREE.Object3D[] = [];
  root.traverse((child) => {
    if (child.name) parts.push(child);
  });

  return {
    root,
    parts,
    setPointing,
    setDoors,
    setExplode,
    setCranes: cranes.update,
    setLasers: (opacity, time) => {
      lasers.material.uniforms.uOpacity.value = opacity;
      lasers.material.uniforms.uTime.value = time;
      lasers.group.visible = opacity > 0.001;
    },
    setLightPath: (opacity, time) => {
      path.material.uniforms.uOpacity.value = opacity;
      path.material.uniforms.uTime.value = time;
      path.lines.visible = opacity > 0.001;
    },
    laserMuzzles: (out) => {
      root.updateMatrixWorld();
      LASER_LAUNCH.forEach((p, i) => {
        out[i] ??= new THREE.Vector3();
        tube.localToWorld(out[i].set(p.x, TOP_Y + TOP_DEPTH + 3.2, p.z));
      });
    },
    dispose: () => {
      site.dispose();
      cranes.dispose();
      disposeTree(root);
    },
  };
}

function disposeTree(object: THREE.Object3D) {
  object.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (mesh.geometry) mesh.geometry.dispose();
    const material = mesh.material as THREE.Material | THREE.Material[] | undefined;
    if (Array.isArray(material)) material.forEach((m) => m.dispose());
    else material?.dispose();
  });
}
